import { useEffect, useRef, useState } from 'react';

import { countScopeValues, startOfMonthISO, useFilterStore } from './filterStore';
import { useAccount } from '../../contexts/AccountContext';
import { SCOPE_KEYS, SCOPE_KEY_LABELS, scopeSuggestions, type RoleGrant, type ScopeKey } from '../../domain/access';
import type { ScopeFilters } from '../../types/extranet-api.types';
import { ChipInput } from '../common/ChipInput';
import { IconChevronDown, IconX } from '../incidents/icons';

const MONTHS = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
];

const toInputValue = (iso: string) => iso.slice(0, 10);
const fromInputValue = (date: string) => new Date(`${date}T00:00:00Z`).toISOString();

const formatRange = (from: string, to: string) => {
  const f = new Date(from);
  const t = new Date(to);
  const fMonth = f.getUTCMonth();
  const tMonth = t.getUTCMonth();
  const fYear = f.getUTCFullYear();
  const tYear = t.getUTCFullYear();
  const fDate = f.getUTCDate();
  const tDate = t.getUTCDate();
  if (fMonth === tMonth && fYear === tYear) {
    return `${fDate}–${tDate} ${MONTHS[fMonth]} ${fYear}`;
  }
  return `${fDate} ${MONTHS[fMonth].slice(0, 4)}. – ${tDate} ${MONTHS[tMonth].slice(0, 4)}. ${tYear}`;
};

const isDefaultDateRange = (from: string) => from.slice(0, 10) === startOfMonthISO().slice(0, 10);

export const GlobalFilters = () => {
  const { scopes, dateRange, setScope, clearScopes, setDateRange, reset } = useFilterStore();
  const { access } = useAccount();

  const hasFilter = countScopeValues(scopes) > 0 || !isDefaultDateRange(dateRange.from);

  return (
    <section aria-label="Filtres globaux" className="flex flex-wrap items-center gap-2">
      <ScopeFiltersButton scopes={scopes} access={access} onChange={setScope} onClear={clearScopes} />

      <DateRangeButton from={dateRange.from} to={dateRange.to} onChange={setDateRange} />

      {hasFilter ? (
        <button
          type="button"
          onClick={reset}
          className="ml-auto text-xs text-zinc-500 hover:text-zinc-900 underline-offset-4 hover:underline transition-colors"
        >
          Réinitialiser
        </button>
      ) : null}
    </section>
  );
};

const usePopover = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  return { open, setOpen, ref };
};

/**
 * Les cinq filtres communs du contrat (`billingReference1..3`, `costCenter`,
 * `serial`) en saisie libre. Pas d'endpoint de facettes : les suggestions sont
 * les valeurs de périmètre déjà attribuées à l'appelant (`GET /v1/users/me`).
 */
const ScopeFiltersButton = ({
  scopes,
  access,
  onChange,
  onClear,
}: {
  scopes: ScopeFilters;
  access: RoleGrant[];
  onChange: (key: ScopeKey, values: string[]) => void;
  onClear: () => void;
}) => {
  const { open, setOpen, ref } = usePopover();
  const count = countScopeValues(scopes);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={[
          'inline-flex items-center gap-2 h-8 px-3 text-sm rounded-md border transition-colors duration-100',
          count > 0
            ? 'bg-zinc-900 text-white border-zinc-900 hover:bg-zinc-800'
            : 'bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400 hover:bg-zinc-50',
        ].join(' ')}
      >
        <span className={count > 0 ? 'text-white' : 'text-zinc-500'}>Périmètre</span>
        <span className="text-xs">{count > 0 ? `${count} valeur${count > 1 ? 's' : ''}` : 'Tout le compte'}</span>
        <IconChevronDown width={12} height={12} className={count > 0 ? 'text-white/70' : 'text-zinc-400'} />
      </button>

      {open ? (
        <div className="absolute top-full mt-1.5 left-0 z-50 w-[400px] bg-white border border-zinc-200 rounded-md shadow-md p-3 flex flex-col gap-3">
          <p className="text-[10px] font-medium uppercase tracking-wide text-zinc-500">
            Restreindre les données affichées
          </p>
          {SCOPE_KEYS.map(key => (
            <label key={key} className="flex flex-col gap-1">
              <span className="text-xs font-medium text-zinc-700">{SCOPE_KEY_LABELS[key]}</span>
              <ChipInput
                id={`scope-filter-${key}`}
                values={scopes[key] ?? []}
                onChange={values => onChange(key, values)}
                suggestions={scopeSuggestions(access, key)}
                placeholder="Saisir une valeur puis Entrée"
              />
            </label>
          ))}
          {count > 0 ? (
            <button
              type="button"
              onClick={onClear}
              className="self-start inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900 transition-colors"
            >
              <IconX width={12} height={12} />
              <span>Tout effacer ({count})</span>
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const DateRangeButton = ({
  from,
  to,
  onChange,
}: {
  from: string;
  to: string;
  onChange: (range: { from: string; to: string }) => void;
}) => {
  const { open, setOpen, ref } = usePopover();

  const applyPreset = (days: number) => {
    const now = new Date();
    const fromDate = new Date(now.getTime() - (days - 1) * 86_400_000);
    fromDate.setUTCHours(0, 0, 0, 0);
    onChange({ from: fromDate.toISOString(), to: now.toISOString() });
    setOpen(false);
  };

  const applyMonthOffset = (offset: number) => {
    const now = new Date();
    const f = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1, 0, 0, 0, 0));
    const t = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset + 1, 0, 23, 59, 59, 999));
    onChange({ from: f.toISOString(), to: t.toISOString() });
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="inline-flex items-center gap-2 h-8 px-3 text-sm rounded-md border border-zinc-300 bg-white text-zinc-700 hover:border-zinc-400 hover:bg-zinc-50 transition-colors duration-100"
      >
        <span className="text-zinc-500">Période</span>
        <span className="font-mono tabular-nums text-xs">{formatRange(from, to)}</span>
        <IconChevronDown width={12} height={12} className="text-zinc-400" />
      </button>

      {open ? (
        <div className="absolute top-full mt-1.5 left-0 z-50 w-80 bg-white border border-zinc-200 rounded-md shadow-md p-3 flex flex-col gap-3">
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wide text-zinc-500 mb-1.5">Raccourcis</p>
            <div className="grid grid-cols-2 gap-1.5">
              <PresetButton onClick={() => applyPreset(7)}>7 derniers jours</PresetButton>
              <PresetButton onClick={() => applyPreset(30)}>30 derniers jours</PresetButton>
              <PresetButton onClick={() => applyMonthOffset(0)}>Mois en cours</PresetButton>
              <PresetButton onClick={() => applyMonthOffset(-1)}>Mois précédent</PresetButton>
              <PresetButton onClick={() => applyPreset(90)}>3 derniers mois</PresetButton>
              <PresetButton onClick={() => applyPreset(365)}>12 derniers mois</PresetButton>
            </div>
          </div>

          <div className="border-t border-zinc-100 pt-3">
            <p className="text-[10px] font-medium uppercase tracking-wide text-zinc-500 mb-2">Période personnalisée</p>
            <div className="grid grid-cols-2 gap-2">
              <DateField
                label="Du"
                value={toInputValue(from)}
                onChange={v => onChange({ from: fromInputValue(v), to })}
                max={toInputValue(to)}
              />
              <DateField
                label="Au"
                value={toInputValue(to)}
                onChange={v => onChange({ from, to: fromInputValue(v) })}
                min={toInputValue(from)}
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const PresetButton = ({ children, onClick }: { children: React.ReactNode; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="h-8 px-2.5 text-xs text-zinc-700 bg-white border border-zinc-200 rounded hover:bg-zinc-50 hover:border-zinc-300 transition-colors text-left"
  >
    {children}
  </button>
);

const DateField = ({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  min?: string;
  max?: string;
}) => (
  <label className="flex flex-col gap-1">
    <span className="text-[10px] font-medium uppercase tracking-wide text-zinc-500">{label}</span>
    <input
      type="date"
      value={value}
      min={min}
      max={max}
      onChange={e => onChange(e.target.value)}
      className="h-8 px-2 text-sm border border-zinc-300 rounded text-zinc-900 font-mono tabular-nums focus:outline-none focus:border-zinc-500 focus:ring-2 focus:ring-zinc-900/10"
    />
  </label>
);
